import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { ALPHA, BADGE, CALENDAR_TODAY_PILL, FONT_SIZE, MOTION } from "@/theme/tokens";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { TOUCH_TARGET_OVERLAY_SX } from "@/ui/touchTarget";
import type { CalendarPager } from "./calendarPager";

type CalendarPagerHeaderProps = {
	pager: CalendarPager;
	title: string;
	isOpen: boolean;
	onTitleClick: () => void;
};

/**
 * A soft brand pill at the arrows' 32px height. Its finger-sized hit area is an invisible overlay
 * (`TOUCH_TARGET_OVERLAY_SX`), so on a phone it stays a 32px pill instead of growing into a 44px slab.
 */
const todayPillSx = (theme: Theme): SystemStyleObject<Theme> => ({
	backgroundColor: alpha(theme.palette.primary.main, CALENDAR_TODAY_PILL.WASH_ALPHA),
	color: theme.palette.primary.dark,
	fontWeight: 600,
	px: 1.5,
	borderRadius: BADGE.PILL_RADIUS_FULL,
	transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
	// SystemButton's own hover is a grey fill and ungated, so a phone would keep it after a tap.
	"&:hover": { backgroundColor: alpha(theme.palette.primary.main, CALENDAR_TODAY_PILL.WASH_ALPHA) },
	"@media (hover: hover) and (pointer: fine)": {
		"&:hover": { backgroundColor: alpha(theme.palette.primary.main, CALENDAR_TODAY_PILL.HOVER_WASH_ALPHA) },
	},
	"&:active": { transform: `scale(${CALENDAR_TODAY_PILL.PRESS_SCALE})` },
	"&.Mui-focusVisible": { boxShadow: `0 0 0 ${CALENDAR_TODAY_PILL.FOCUS_RING_PX}px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
	"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
});

/**
 * The paged calendar's header: the page's months on the leading side (tapping them folds the calendar away), and the
 * paging arrows on the trailing side, with a Today pill in front of them whenever the selected day isn't today.
 *
 * Today appears in the space the title leaves, so nothing else in the row moves when it comes and goes. A centred
 * title can't stay centred once Today sits beside an arrow: a two-month title ("Oct - Nov 2026") leaves too little
 * room on a 390px phone. On a 320px phone with both a two-month title and Today, the title ellipsizes rather than
 * pushing the arrows off the screen.
 */
export const CalendarPagerHeader: FC<CalendarPagerHeaderProps> = ({ pager, title, isOpen, onTitleClick }) => {
	const { t } = useTranslation();
	const prefersReducedMotion = useReducedMotion();
	const hiddenToday = prefersReducedMotion ? { opacity: 0 } : { opacity: 0, transform: `scale(${CALENDAR_TODAY_PILL.ENTER_SCALE})` };

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.5 }}>
			<SystemButton
				buttonVariant="borderless"
				noFullWidthMobile
				touchTarget
				onClick={onTitleClick}
				aria-expanded={isOpen}
				data-testid="calendar-pager-title"
				sx={{ fontSize: FONT_SIZE.TITLE_MD, fontWeight: 600, gap: 0.5, minWidth: 0, px: 0.5 }}
			>
				<Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
					{title}
				</Box>
				{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
			</SystemButton>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, ml: "auto" }}>
				<AnimatePresence initial={false}>
					{pager.isAwayFromToday && (
						<motion.div
							key="today"
							initial={hiddenToday}
							animate={{ opacity: 1, transform: "scale(1)" }}
							exit={{ ...hiddenToday, transition: { duration: MOTION.DURATION.INLINE_EXIT / 1000, ease: MOTION.EASE_OUT_POINTS } }}
							transition={{ duration: MOTION.DURATION.ENTRANCE_SM / 1000, ease: MOTION.EASE_OUT_POINTS }}
							style={{ display: "flex" }}
						>
							<SystemButton
								size="compact"
								buttonVariant="borderless"
								noFullWidthMobile
								onClick={pager.goToday}
								data-testid="calendar-pager-today"
								sx={[todayPillSx, TOUCH_TARGET_OVERLAY_SX]}
							>
								{t("staff.schedule.today")}
							</SystemButton>
						</motion.div>
					)}
				</AnimatePresence>
				<SystemButton
					iconButton
					touchTarget
					buttonVariant="borderless"
					aria-label={t("staff.schedule.previous.weeks", { weeks: pager.weekCount })}
					disabled={!pager.canGoPrev}
					onClick={pager.goPrev}
					data-testid="calendar-pager-prev"
				>
					<ChevronLeftIcon />
				</SystemButton>
				<SystemButton
					iconButton
					touchTarget
					buttonVariant="borderless"
					aria-label={t("staff.schedule.next.weeks", { weeks: pager.weekCount })}
					disabled={!pager.canGoNext}
					onClick={pager.goNext}
					data-testid="calendar-pager-next"
				>
					<ChevronRightIcon />
				</SystemButton>
			</Box>
		</Box>
	);
};
